import { Fragment } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import {
  buildBodyParagraphAttrs,
  reindentBlocksToLevel,
  resolveIndentFromTopOfListItem,
} from './post_block_indent.js';
import {
  findListItemDepth,
  findResourceTableDepth,
  findSubtitleChildIndex,
  isAtResourceRowTitleStart,
  isResourceRowNode,
  lastTitleChildIndex,
  listItemChildStartPos,
  mergeBackspaceFromParagraphBelow,
  paragraphIsEffectivelyEmpty,
  paragraphTextEndPos,
  resourceRowHasSubtitle,
  rowChildStartPos,
  unwrapResourceRowAtTitleStart,
} from './resource_row_paragraph.js';

const MEDIA_BLOCK_TYPES = new Set(['image', 'video', 'embedVideo', 'postImageCarousel']);

function listItemHasMedia(listItemNode) {
  let found = false;
  listItemNode.forEach((child) => {
    if (MEDIA_BLOCK_TYPES.has(child.type.name)) found = true;
  });
  return found;
}

function listItemHasResourceTable(listItemNode) {
  let found = false;
  listItemNode.forEach((child) => {
    if (child.type.name === 'resourceTable') found = true;
  });
  return found;
}

function findMediaAncestor($from) {
  for (let d = $from.depth; d > 0; d--) {
    const name = $from.node(d).type.name;
    if (MEDIA_BLOCK_TYPES.has(name)) {
      return { node: $from.node(d), pos: $from.before(d), depth: d };
    }
  }
  return null;
}

function isEmptyParagraph(node) {
  if (node.type.name !== 'paragraph') return false;
  return (
    node.content.size === 0 ||
    (node.content.size <= 2 && node.textContent.trim() === '')
  );
}

/** First paragraph before any media block (intro copy in a list item). */
function bodyParagraphBeforeMedia(listItemNode) {
  let node = null;
  let offset = -1;
  listItemNode.forEach((child, _offset, index) => {
    if (MEDIA_BLOCK_TYPES.has(child.type.name)) return;
    if (node) return;
    if (child.type.name === 'paragraph') {
      node = child;
      offset = index;
    }
  });
  return { node, offset };
}

/** First paragraph that appears after a media block in the same list item. */
function paragraphAfterMedia(listItemNode) {
  let seenMedia = false;
  let node = null;
  let offset = -1;
  listItemNode.forEach((child, _offset, index) => {
    if (MEDIA_BLOCK_TYPES.has(child.type.name)) {
      seenMedia = true;
      return;
    }
    if (seenMedia && !node && child.type.name === 'paragraph') {
      node = child;
      offset = index;
    }
  });
  return { node, offset };
}

function insertPosAtEndOfListItem(listItemPos, listItemNode) {
  let pos = listItemPos + 1;
  listItemNode.forEach((child) => {
    pos += child.nodeSize;
  });
  return pos;
}

/** Index of the last image/video/carousel block inside a list item. */
function lastMediaChildIndex(listItemNode) {
  let lastIdx = -1;
  listItemNode.forEach((child, _offset, childIndex) => {
    if (MEDIA_BLOCK_TYPES.has(child.type.name)) lastIdx = childIndex;
  });
  return lastIdx;
}

/** Doc position immediately after a specific child block in a list item. */
function insertPosAfterListItemChild(listItemPos, listItemNode, childIndex) {
  const child = listItemNode.child(childIndex);
  return listItemChildStartPos(listItemPos, listItemNode, childIndex) + child.nodeSize;
}

/** Where leading paragraph text from a merged list item should be inserted. */
function resolveListItemTextMergeTarget(prevItemPos, prevListItem) {
  const afterMedia = paragraphAfterMedia(prevListItem);
  if (afterMedia.node) {
    return {
      kind: 'paragraph_end',
      paraPos: listItemChildStartPos(prevItemPos, prevListItem, afterMedia.offset),
      paragraph: afterMedia.node,
    };
  }

  const mediaIdx = lastMediaChildIndex(prevListItem);
  if (mediaIdx >= 0) {
    return {
      kind: 'new_paragraph_after',
      insertPos: insertPosAfterListItemChild(prevItemPos, prevListItem, mediaIdx),
    };
  }

  let lastTableIdx = -1;
  prevListItem.forEach((child, _offset, index) => {
    if (child.type.name === 'resourceTable') lastTableIdx = index;
  });
  if (lastTableIdx >= 0) {
    const table = prevListItem.child(lastTableIdx);
    const tablePos = listItemChildStartPos(prevItemPos, prevListItem, lastTableIdx);
    const lastRow = table.child(table.childCount - 1);
    if (isResourceRowNode(lastRow)) {
      let rowPos = tablePos + 1;
      for (let i = 0; i < table.childCount - 1; i++) {
        rowPos += table.child(i).nodeSize;
      }
      const targetIndex = resourceRowHasSubtitle(lastRow)
        ? findSubtitleChildIndex(lastRow)
        : lastTitleChildIndex(lastRow);
      if (targetIndex >= 0) {
        return {
          kind: 'paragraph_end',
          paraPos: rowChildStartPos(rowPos, lastRow, targetIndex),
          paragraph: lastRow.child(targetIndex),
        };
      }
    }
  }

  let lastParaIdx = -1;
  let lastPara = null;
  prevListItem.forEach((child, _offset, index) => {
    if (child.type.name === 'paragraph') {
      lastParaIdx = index;
      lastPara = child;
    }
  });
  if (lastParaIdx >= 0 && lastPara) {
    return {
      kind: 'paragraph_end',
      paraPos: listItemChildStartPos(prevItemPos, prevListItem, lastParaIdx),
      paragraph: lastPara,
    };
  }

  return {
    kind: 'end_of_item',
    insertPos: insertPosAtEndOfListItem(prevItemPos, prevListItem),
  };
}

/** Leading paragraph text + every other block in the list item (tables, media, etc.). */
function collectListItemMergePayload(listItemNode) {
  let leadingParagraph = null;
  const trailingBlocks = [];
  listItemNode.forEach((child) => {
    if (child.type.name === 'paragraph' && leadingParagraph === null) {
      leadingParagraph = child;
      return;
    }
    trailingBlocks.push(child);
  });
  return {
    incomingContent: leadingParagraph?.content ?? Fragment.empty,
    trailingBlocks,
  };
}

/** Backspace at start of a paragraph directly below a resource table in a list item. */
function backspaceParagraphAfterResourceTableInList(editor, $from, tablePos, tableNode) {
  if (!tableNode?.childCount) return false;

  if (isEmptyParagraph($from.parent)) {
    return editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        tr.delete($from.before($from.depth), $from.after($from.depth));
        const mappedTablePos = tr.mapping.map(tablePos);
        const updatedTable = tr.doc.nodeAt(mappedTablePos);
        if (updatedTable?.childCount > 0) {
          const lastRow = updatedTable.child(updatedTable.childCount - 1);
          let rowOffset = 1;
          for (let i = 0; i < updatedTable.childCount - 1; i++) {
            rowOffset += updatedTable.child(i).nodeSize;
          }
          const lastRowPos = mappedTablePos + rowOffset;
          if (isResourceRowNode(lastRow)) {
            const targetIndex = resourceRowHasSubtitle(lastRow)
              ? findSubtitleChildIndex(lastRow)
              : lastTitleChildIndex(lastRow);
            if (targetIndex >= 0) {
              const targetParagraph = lastRow.child(targetIndex);
              const targetParaPos = rowChildStartPos(lastRowPos, lastRow, targetIndex);
              const cursorPos = paragraphTextEndPos(targetParaPos, targetParagraph);
              try {
                tr.setSelection(TextSelection.create(tr.doc, cursorPos));
              } catch {
                tr.setSelection(TextSelection.near(tr.doc.resolve(cursorPos)));
              }
            }
          }
        }
        if (dispatch) dispatch(tr);
        return true;
      })
      .run();
  }

  const lastRow = tableNode.child(tableNode.childCount - 1);
  if (!isResourceRowNode(lastRow)) return false;
  let rowPos = tablePos + 1;
  for (let i = 0; i < tableNode.childCount - 1; i++) {
    rowPos += tableNode.child(i).nodeSize;
  }
  const targetIndex = resourceRowHasSubtitle(lastRow)
    ? findSubtitleChildIndex(lastRow)
    : lastTitleChildIndex(lastRow);
  if (targetIndex < 0) return false;
  const targetParagraph = lastRow.child(targetIndex);
  const targetStart = rowChildStartPos(rowPos, lastRow, targetIndex);
  return mergeBackspaceFromParagraphBelow(editor, $from, targetStart, targetParagraph);
}

/**
 * Merge the current list item into the previous list item: intro text joins the
 * appropriate target (after media, last row title, or last paragraph); trailing
 * blocks (resource tables, etc.) append at the end of the previous item.
 */
function joinListItemIntoListItemAbove(editor, $from) {
  const listItemDepth = findListItemDepth($from);
  if (listItemDepth < 0) return false;

  const listDepth = listItemDepth - 1;
  const indexInList = $from.index(listDepth);
  if (indexInList <= 0) return false;

  const prevListItem = $from.node(listDepth).child(indexInList - 1);
  const currentItemPos = $from.before(listItemDepth);
  const currentItem = $from.node(listItemDepth);
  const prevItemPos = currentItemPos - prevListItem.nodeSize;
  const deleteFrom = currentItemPos;
  const deleteTo = currentItemPos + currentItem.nodeSize;

  const { incomingContent, trailingBlocks } = collectListItemMergePayload(currentItem);
  const textTarget = resolveListItemTextMergeTarget(prevItemPos, prevListItem);
  const { state, view } = editor;
  const paragraphType = state.schema.nodes.paragraph;
  if (!paragraphType) return false;

  const parentIndent = resolveIndentFromTopOfListItem(prevListItem);
  const mergedParaAttrs = buildBodyParagraphAttrs(parentIndent);
  const trailingBlocksNormalized = reindentBlocksToLevel(trailingBlocks, parentIndent);

  let tr = state.tr;
  let cursorPos;

  if (textTarget.kind === 'paragraph_end') {
    const textInsertPos = paragraphTextEndPos(textTarget.paraPos, textTarget.paragraph);
    if (incomingContent.size > 0) {
      tr = tr.insert(textInsertPos, incomingContent);
    }
    cursorPos = tr.mapping.map(textInsertPos) + Math.max(0, incomingContent.size);
  } else if (textTarget.kind === 'new_paragraph_after') {
    if (incomingContent.size > 0) {
      const mergedParagraph = paragraphType.create(mergedParaAttrs, incomingContent);
      tr = tr.insert(textTarget.insertPos, mergedParagraph);
      cursorPos = tr.mapping.map(textTarget.insertPos) + 1 + incomingContent.size;
    } else {
      cursorPos = tr.mapping.map(textTarget.insertPos);
    }
  } else if (textTarget.kind === 'end_of_item') {
    if (incomingContent.size > 0) {
      const mergedParagraph = paragraphType.create(mergedParaAttrs, incomingContent);
      tr = tr.insert(textTarget.insertPos, mergedParagraph);
      cursorPos = tr.mapping.map(textTarget.insertPos) + 1 + incomingContent.size;
    } else {
      cursorPos = tr.mapping.map(textTarget.insertPos);
    }
  } else {
    return false;
  }

  if (trailingBlocksNormalized.length > 0) {
    const mappedPrevPos = tr.mapping.map(prevItemPos);
    const mappedPrevItem = tr.doc.nodeAt(mappedPrevPos);
    if (!mappedPrevItem) return false;
    const insertAt = insertPosAtEndOfListItem(mappedPrevPos, mappedPrevItem);
    tr = tr.insert(insertAt, Fragment.from(trailingBlocksNormalized));
    let tailEnd = insertAt;
    trailingBlocksNormalized.forEach((node) => {
      tailEnd += node.nodeSize;
    });
    cursorPos = Math.max(1, tailEnd - 1);
  }

  tr = tr.delete(tr.mapping.map(deleteFrom), tr.mapping.map(deleteTo));

  try {
    tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
  } catch {
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(cursorPos)));
  }

  view.dispatch(tr.scrollIntoView());
  editor.commands.focus();
  return true;
}

/**
 * Backspace at the start of an empty paragraph inside a list item (not the first
 * block in that item). Removes only that paragraph — avoids liftListItem / joinItemBackward
 * breaking the list (common after multi-line paste into one item).
 */
export function backspaceEmptyParagraphInListItem(editor, $from) {
  const listItemDepth = findListItemDepth($from);
  if (listItemDepth < 0) return false;
  if ($from.parent.type.name !== 'paragraph' || $from.parentOffset !== 0) return false;
  if (!paragraphIsEffectivelyEmpty($from.parent)) return false;

  const indexInListItem = $from.index(listItemDepth);
  if (indexInListItem <= 0) return false;

  const listItem = $from.node(listItemDepth);
  const listItemPos = $from.before(listItemDepth);
  const prevSibling = listItem.child(indexInListItem - 1);
  if (prevSibling.type.name !== 'paragraph') return false;

  const prevStart = listItemChildStartPos(listItemPos, listItem, indexInListItem - 1);
  const cursorPos = paragraphTextEndPos(prevStart, prevSibling);
  const paraStart = $from.before($from.depth);
  const paraEnd = paraStart + $from.parent.nodeSize;

  return editor
    .chain()
    .focus()
    .command(({ tr, dispatch }) => {
      tr.delete(paraStart, paraEnd);
      const mapped = tr.mapping.map(cursorPos, -1);
      tr.setSelection(TextSelection.create(tr.doc, Math.max(1, mapped)));
      if (dispatch) dispatch(tr);
      return true;
    })
    .run();
}

export function backspaceNearMediaInList(editor, $from) {
  const listItemDepth = findListItemDepth($from);
  if (listItemDepth < 0) return false;

  const media = findMediaAncestor($from);
  if (media) {
    const atCaptionStart =
      $from.pos <= media.pos + 1 ||
      ($from.parentOffset === 0 && $from.depth > media.depth);
    if (!atCaptionStart) return false;

    if (media.node.content.size === 0 || media.node.textContent.length === 0) {
      return editor.chain().focus().setNodeSelection(media.pos).run();
    }

    return true;
  }

  if ($from.parent.type.name !== 'paragraph' || $from.parentOffset !== 0) {
    return false;
  }

  const listItem = $from.node(listItemDepth);
  const indexInListItem = $from.index(listItemDepth);

  if (indexInListItem > 0) {
    const prevSibling = listItem.child(indexInListItem - 1);
    if (prevSibling.type.name === 'resourceTable') {
      const listItemPos = $from.before(listItemDepth);
      const tablePos = listItemChildStartPos(listItemPos, listItem, indexInListItem - 1);
      if (backspaceParagraphAfterResourceTableInList(editor, $from, tablePos, prevSibling)) {
        return true;
      }
      return true;
    }
    if (MEDIA_BLOCK_TYPES.has(prevSibling.type.name)) {
      const listItemPos = $from.before(listItemDepth);

      if (isEmptyParagraph($from.parent)) {
        return editor
          .chain()
          .focus()
          .command(({ tr, dispatch }) => {
            tr.delete($from.before($from.depth), $from.after($from.depth));
            const body = bodyParagraphBeforeMedia(listItem);
            if (body.node) {
              const paraPos = listItemChildStartPos(listItemPos, listItem, body.offset);
              const cursorPos = paragraphTextEndPos(paraPos, body.node);
              tr.setSelection(TextSelection.create(tr.doc, cursorPos));
            } else {
              const mediaPos = listItemChildStartPos(
                listItemPos,
                listItem,
                indexInListItem - 1
              );
              tr.setSelection(NodeSelection.create(tr.doc, mediaPos));
            }
            if (dispatch) dispatch(tr);
            return true;
          })
          .run();
      }

      // Paragraph directly after media: do not merge into intro above the video.
      return true;
    }
  }

  if (findResourceTableDepth($from) >= 0) {
    if (isAtResourceRowTitleStart($from)) {
      return unwrapResourceRowAtTitleStart(editor, $from) || true;
    }
    if ($from.parent.type.name === 'paragraph' && $from.parentOffset === 0) {
      return true;
    }
    return false;
  }

  const listDepth = listItemDepth - 1;
  const indexInList = $from.index(listDepth);

  // Intro paragraph before a resource table — merge whole list item into the one above.
  if (
    listItemHasResourceTable(listItem) &&
    $from.index(listItemDepth) === 0 &&
    indexInList > 0
  ) {
    if (joinListItemIntoListItemAbove(editor, $from)) return true;
    // Block joinItemBackward — it drops the resource table and only merges text.
    return true;
  }

  if (indexInList > 0) {
    const prevListItem = $from.node(listDepth).child(indexInList - 1);
    // joinItemBackward merges into the intro paragraph above media; keep text after carousel.
    if (listItemHasMedia(prevListItem)) {
      if (joinListItemIntoListItemAbove(editor, $from)) return true;
      return true;
    }
  }

  if (editor.can().joinItemBackward()) {
    return editor.chain().focus().joinItemBackward().run();
  }

  return false;
}
